import type { ReactNode } from 'react';
import { useAppwideDrawerContext } from '../../../../providers/AppwideDrawerProvider';
import type { ButtonVariants } from '../../../Button';
import { Box } from '../../../Core';
import type { EntityAdminRecord } from '../../types';
import {
  EntityAdminWorkflowStepper,
  type EntityAdminWorkflowStep,
} from '../EntityAdminWorkflowStepper/EntityAdminWorkflowStepper';
import type { InputsAndDropdownsDrawerOptions } from '../InputsAndDropdownsDrawer/types';
import { WorkflowDrawerLayout } from './WorkflowDrawerLayout';
import { WorkflowFormStep } from './WorkflowFormStep';

/**
 * A single step in a multi-step workflow. Extends the stepper metadata (id, label, icon)
 * with everything needed to render a WorkflowFormStep.
 */
export interface WorkflowStepDefinition<
  TForm extends EntityAdminRecord,
  TStepId extends string = string,
> extends EntityAdminWorkflowStep<TStepId> {
  drawerOptions: InputsAndDropdownsDrawerOptions<any>;
  saveLabel?: string;
  saveVariant?: ButtonVariants;
  summary?: ReactNode;
  loading?: boolean;
  onSave: (form: TForm) => Promise<void> | void;
}

export interface WorkflowFormStepsProps<TForm extends EntityAdminRecord, TStepId extends string = string> {
  steps: readonly WorkflowStepDefinition<TForm, TStepId>[];
  currentStepId: TStepId;
  form: TForm;
  onSelectStep: (stepId: TStepId) => void;
  onFormStateChange: (form: TForm) => void;
}

/**
 * Renders a multi-step workflow: stepper header + the active form step.
 * Wraps WorkflowDrawerLayout; intended for use inside the app-wide drawer.
 * Handles cancel internally via useAppwideDrawerContext.
 */
export function WorkflowFormSteps<TForm extends EntityAdminRecord, TStepId extends string>({
  steps,
  currentStepId,
  form,
  onSelectStep,
  onFormStateChange,
}: WorkflowFormStepsProps<TForm, TStepId>) {
  const { closeDrawer } = useAppwideDrawerContext();

  return (
    <WorkflowDrawerLayout>
      <Box flex="none" background="colors.gray.020" borderBottom="1px solid" borderColor="colors.gray.000">
        <EntityAdminWorkflowStepper steps={steps} currentStepId={currentStepId} onSelectStep={onSelectStep} />
      </Box>
      {steps.map(step =>
        currentStepId === step.id ? (
          <WorkflowFormStep
            key={step.id}
            drawerOptions={step.drawerOptions as InputsAndDropdownsDrawerOptions<TForm>}
            initialForm={form}
            summary={step.summary}
            saveLabel={step.saveLabel ?? 'Save & Continue'}
            saveVariant={step.saveVariant}
            loading={step.loading}
            onCancel={closeDrawer}
            onSave={step.onSave}
            onFormStateChange={onFormStateChange}
          />
        ) : null
      )}
    </WorkflowDrawerLayout>
  );
}
